import * as React from 'react';
import bindAllOfThis from 'src/utils/BindThisHelper';
import { Icon, Row, Col } from "antd";
import "./AudioPlayer.less";
import DebugUtil from "src/utils/DebugUtil";
import { ReduxStoreModel } from 'src/redux/ReduxModel';
import { connect } from 'react-redux';

interface AudioPlayerProps {
    audioURL: string;
    /**
     * Changes playback from playing to paused with the flick of a switch!
     *
     * If true when the URL or playRange is changed, the audio will reload and change time and start playing. False will do the same, but pause the audio
     */
    isPlaying: boolean;
    /**
     * Plays the audio from start time to end time. Time is relative to the audio track in seconds
     *
     * Eg.
     *
     *    { start: 3, end: 14.53 }
     *
     * Will start at 3 seconds and play till the time reads 14.53
     *
     *    { start: 8.33 }
     *
     * Will start at 8330ms and play till the end
     */
    playRange?: { start: number, end?: number };

    /** Called we begin playback */
    onPlay?(): void;
    /** Called we pause playback */
    onPause?(): void;
    /** Called playback has completed */
    onComplete?(): void;
    isEnabled: boolean;
}
interface AudioPlayerState {
    currentTime: number;
    isPlaying: boolean;
    isMuted: boolean;
}

class AudioPlayer extends React.Component<AudioPlayerProps, AudioPlayerState> {
    audioElement: HTMLAudioElement;
    timer: NodeJS.Timer;
    isLoading: boolean = false; // This changes too quickly for setState
    constructor(props: AudioPlayerProps) {
        super(props);
        bindAllOfThis(this, AudioPlayer.prototype);

        let audioElement = new Audio();
        audioElement.autoplay = false;
        audioElement.preload = "auto";

        // Add watchers
        audioElement.addEventListener("timeupdate", this.audio_timeupdate);
        audioElement.addEventListener("ended", this.audio_ended);

        this.audioElement = audioElement;
        this.timer = null;

        this.state = {
            currentTime: 0,
            isPlaying: this.props.isEnabled,
            isMuted: false,
        };
    }

    componentDidMount() {
        this.reloadComponent(this.props);
    }

    componentWillReceiveProps(nextProps: AudioPlayerProps) {
        if (this.props.audioURL !== nextProps.audioURL) {
            // URL changed, hard reload everything
            this.reloadComponent(nextProps);
        } else if (this.props.playRange !== nextProps.playRange) {
            // State changed, do what they asked
            this.updateStartLocation(nextProps);
            this.updatePlayback(nextProps);
        } else if (this.props.isPlaying !== nextProps.isPlaying || this.props.isEnabled !== nextProps.isEnabled) {
            // State changed, do what they asked
            this.updatePlayback(nextProps);
        }
    }

    componentWillUnmount() {
        this.audioElement.removeEventListener("timeupdate", this.audio_timeupdate);
        this.audioElement.removeEventListener("ended", this.audio_ended);
        if (this.audioElement != null) {
            this.audioElement.pause();
        }
    }

    private audio_timeupdate() {
        if (DebugUtil.isDebugEnabled()) {
            // This will update the time
            this.setState({ currentTime: this.audioElement.currentTime });
        }
        if (this.props.playRange == null || this.props.playRange.end == null) {
            return;
        }

        // Are we less than half a second from stopping?
        if (this.timer == null && this.audioElement.currentTime + .5 > this.props.playRange.end) {
            let timeout = Math.floor((this.props.playRange.end - this.audioElement.currentTime) * 100) * 10;
            this.timer = setTimeout(() => {
                this.audioElement.pause();
                this.setState({ currentTime: this.audioElement.currentTime, isPlaying: false });
                if (this.props.onComplete != null) {
                    this.props.onComplete();
                }
                // Set timer object to null, but not too quickly
                setTimeout(() => this.timer = null, 333);
            }, timeout);
        }
    }

    private audio_ended() {
        // Indicates that the audio completed playing.
        // Does not indicate that the audio has paused, only that it completed
        if (this.props.onComplete != null) {
            this.props.onComplete();
        }
        this.setState({ currentTime: this.audioElement.currentTime, isPlaying: false });
    }

    private reloadComponent(props: AudioPlayerProps) {
        // Load the audio and update playback status
        if (props.audioURL == null || props.audioURL.length < 8) { // http:// is 7 chars long
            this.audioElement.src = "";
            this.audioElement.currentTime = 0;
            this.pauseAudio();
            return;
        }
        this.changeSrc(props);
        this.updateStartLocation(props);
        this.updatePlayback(props);
    }

    private changeSrc(props: AudioPlayerProps) {
        if (props.audioURL == null || props.audioURL.length < 1) {
            return;
        }
        this.audioElement.src = props.audioURL;
    }

    private updateStartLocation(props: AudioPlayerProps) {
        // Set start time to the start time
        // We assume that most changes to this property will start at the beginning or beginning of a break
        if (props.playRange != null && props.playRange.start != null) {
            this.audioElement.currentTime = props.playRange.start;
        } else {
            this.audioElement.currentTime = 0;
        }
    }

    private updatePlayback(props: AudioPlayerProps) {
        if (!props.isEnabled) {
            this.pauseAudio();
        }
        // Changing isPlaying or adding an end time should immediately pause the audio. Play otherwise
        else if (!props.isPlaying || (props.playRange != null && props.playRange.end != null && this.audioElement.currentTime >= props.playRange.end)) {
            this.pauseAudio();
        } else {
            this.playAudio();
        }
        this.setState({ currentTime: this.audioElement.currentTime, isPlaying: !this.audioElement.paused });
    }

    private playAudio() {
        if (this.audioElement.src == null || this.audioElement.src.length < 1) {
            return;
        }

        this.isLoading = true;
        this.audioElement.play().then(() => {
            this.isLoading = false;

            if (this.props.onPlay != null) {
                this.props.onPlay();
            }
            this.setState({ currentTime: this.audioElement.currentTime, isPlaying: !this.audioElement.paused });
        });
    }

    private pauseAudio() {
        this.audioElement.pause();
        this.setState({ currentTime: this.audioElement.currentTime, isPlaying: !this.audioElement.paused });
        if (this.props.onPause != null) {
            this.props.onPause();
        }
    }

    private resetAudio() {
        this.audioElement.pause();
        this.reloadComponent(this.props);
    }

    private muteAudio() {
        this.audioElement.muted = !this.state.isMuted;
        this.setState({ isMuted: !this.state.isMuted });
    }

    private advanceTime(time: number) {
        this.audioElement.currentTime += time;
    }

    renderPlayRange() {
        if (this.props.playRange == null) {
            return null;
        }
        if (this.props.playRange.end != null) {
            return <span>Start: {this.props.playRange.start} | End: {this.props.playRange.end}</span>;
        }
        return <span>Start: {this.props.playRange.start}</span>;
    }

    render() {
        return null;
        if (DebugUtil.isDebugEnabled()) {
            // Things!
            let splits = this.props.audioURL.split('/');
            let split = splits.find(x => x.includes(".mp3"));
            let mp3String = "";
            if (split != null) {
                let mp3Index = split.indexOf(".mp3");
                let startIndex = split.indexOf('/', mp3Index);
                startIndex = startIndex < 0 ? 0 : startIndex;
                mp3String = split.substring(startIndex, mp3Index + 4);
            }

            return <div className="audio-player">
            <Row>
                <Col span={8}>
                {
                    this.state.isPlaying
                        ? <Icon type="pause-circle" onClick={this.pauseAudio} />
                        : <Icon type="play-circle" onClick={this.playAudio} />
                }
                <Icon type="sync" onClick={this.resetAudio} />
                <Icon type="stop" onClick={this.muteAudio} theme="twoTone" twoToneColor="#52c41a" />
                <Icon type="plus-circle" onClick={() => this.advanceTime(5)} theme="twoTone" twoToneColor="#52c41a" />
                <Icon type="minus-circle" onClick={() => this.advanceTime(-5)} theme="twoTone" twoToneColor="#52c41a" />
                </Col>
                <Col span={16}>
                Audio File: {mp3String}
                <br />
                <span className="current-time">Current Time: {this.state.currentTime}</span>
                {this.renderPlayRange()}
                </Col>
            </Row>
            </div>;
        }
        return null;
    }
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        isEnabled: state.Config.audioEnabled,
    };
}

export default connect(mapStateToProps)(AudioPlayer);



// WEBPACK FOOTER //
// ./src/components/common/AudioPlayer.tsx